import { useRef, useState } from 'react';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type {
	AssetEntity,
	AssetOption,
} from '@atlassian/jira-issue-field-assets/src/common/types.tsx';
import { mapAssetEntityToAssetOptions } from '@atlassian/jira-issue-field-assets/src/ui/edit/utils.tsx';
import type { AssetEntityForServer, FieldValue, JiraAssetsFieldInput } from './types.tsx';

// Used when submitting
export const mapAssetOptionsToAssetEntity = (options: AssetOption[]): AssetEntityForServer[] =>
	options.map(({ value }) => ({
		appKey: value.split('/')[0],
		originId: value.split('/')[1],
		serializedOrigin: value,
		value,
	}));

// Used when retaining input on project/issue-type change
export const mutateAssetsFields = (
	selectedAssets: FieldValue,
	fieldId: string,
): JiraAssetsFieldInput => ({
	assetsField: {
		fieldId,
		assets: selectedAssets.map(({ value }) => ({
			appKey: value.split('/')[0],
			originId: value.split('/')[1],
			serializedOrigin: value,
			value,
		})),
	},
});

// Transform retained data from mutateAssetsFields into the AssetField again
export const transformDefaultAssets = (defaultAssets: AssetEntityForServer[]): FieldValue =>
	defaultAssets.map(({ serializedOrigin }) => ({
		value: serializedOrigin,
	}));

export const useFetchAssetsWithId = (): readonly [
	{
		readonly isLoading: boolean;
	},
	(ids: string[], setSelectedOptions: (arg1: AssetOption[]) => void) => void,
] => {
	const [isLoading, setLoading] = useState<boolean>(false);

	const handleFetch = useRef((ids: string[], setSelectedOptions: (arg1: AssetOption[]) => void) => {
		setLoading(true);
		Promise.all(ids.map((id) => fetchJson(`/rest/assetapi/asset/${id}`)))
			.then((response: AssetEntity[]) => {
				setLoading(false);
				setSelectedOptions(mapAssetEntityToAssetOptions(response));
			})
			.catch((error) => {
				setLoading(false);
				setSelectedOptions([]);
				fireErrorAnalytics({
					meta: {
						id: 'useFetchAssetsWithId',
						packageName: 'jiraIssueCreateCommons',
						teamName: 'gryffindor',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			});
	});
	return [{ isLoading }, handleFetch.current] as const;
};
